import React, { useEffect, useState } from 'react';
import { GuideOpenBody } from '../../../../../interfaces/stay-guide';
import { t } from '../../../../onboarding/stay/stay-strings';
import { GuideH1, useGuideNav } from './guide-frame';
import { At, langOf, timeIn } from './guide-format';
import { CheckItem, CheckMark, Checklist, HostText, Panel, SectionHeading, Stack } from './guide.styled';

const L = 'en' as const;

/**
 * CHECK-OUT (plan S7.7): "Check-out by 11:00" (the building's clock), the
 * host's instructions, the checklist as toggles, and quiet hours.
 *
 * THE TICKS ARE A PER-VIEWER CONVENIENCE, kept in sessionStorage under a key
 * made from a short NON-REVERSIBLE hash of the token (never the token, never
 * a code) together with a hash of the list itself, so a host editing the list
 * starts it fresh. Every storage access is inside try/catch: a private window
 * or blocked storage simply keeps the ticks for this page view. This is the
 * ONLY guide file allowed to touch sessionStorage (no-persist.guard.test.ts).
 * The host preview keeps them in memory only.
 */

/** FNV-1a, 32 bit, hex: short and one-way enough for a storage key. */
export const shortHash = (text: string): string => {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i += 1) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }

  return h.toString(16).padStart(8, '0');
};

export const ticksKey = (token: string): string => `lobbero.guide.ticks.${shortHash(`stay-guide:${token}`)}`;

interface Stored {
  list: string;
  on: number[];
}

const readTicks = (token: string, items: string[]): boolean[] => {
  const none = items.map(() => false);
  if (!token) return none;
  try {
    const raw = window.sessionStorage.getItem(ticksKey(token));
    if (!raw) return none;
    const parsed = JSON.parse(raw) as Partial<Stored> | null;
    if (!parsed || parsed.list !== shortHash(items.join('\n')) || !Array.isArray(parsed.on)) return none;

    return items.map((_, i) => (parsed.on as number[]).includes(i));
  } catch {
    return none;
  }
};

const writeTicks = (token: string, items: string[], on: boolean[]): void => {
  if (!token) return;
  try {
    const stored: Stored = {
      list: shortHash(items.join('\n')),
      on: on.map((v, i) => (v ? i : -1)).filter(i => i >= 0),
    };
    window.sessionStorage.setItem(ticksKey(token), JSON.stringify(stored));
  } catch {
    // Private mode or blocked storage: the ticks live for this page view only.
  }
};

export const GuideCheckout: React.FC<{ body: GuideOpenBody }> = ({ body }) => {
  const nav = useGuideNav();
  const { guide, window: w, zone } = body;
  const lang = langOf(guide.content_language);
  const items = guide.checkout_checklist.filter(item => item.trim());
  const listSig = items.join('\n');
  const storeToken = nav.preview ? '' : nav.token;
  const [on, setOn] = useState<boolean[]>(() => readTicks(storeToken, items));

  // A refetch that changed the list: read it again (a new list starts fresh).
  useEffect(() => {
    setOn(readTicks(storeToken, listSig ? listSig.split('\n') : []));
  }, [storeToken, listSig]);

  const toggle = (index: number, next: boolean) => {
    setOn(current => {
      const updated = items.map((_, i) => (i === index ? next : !!current[i]));
      writeTicks(storeToken, items, updated);

      return updated;
    });
  };

  const [before, after] = t(L, 'guide.checkoutTitle').split('{time}');

  return (
    <>
      <GuideH1>
        {before}
        <At iso={w.check_out_at}>{timeIn(w.check_out_at, zone)}</At>
        {after}
      </GuideH1>

      {guide.checkout_instructions ? <HostText lang={lang}>{guide.checkout_instructions}</HostText> : null}

      {items.length ? (
        <Stack as="section" aria-labelledby="guide-checklist-title">
          <SectionHeading id="guide-checklist-title">{t(L, 'guide.checklistTitle')}</SectionHeading>
          <Checklist>
            {items.map((item, i) => (
              <li key={`${i}-${item}`}>
                <CheckItem pressed={!!on[i]} onToggle={next => toggle(i, next)} lang={lang}>
                  <CheckMark $on={!!on[i]} aria-hidden="true">
                    {on[i] ? '✓' : ''}
                  </CheckMark>
                  {item}
                </CheckItem>
              </li>
            ))}
          </Checklist>
        </Stack>
      ) : null}

      {guide.quiet_hours ? (
        <Panel aria-labelledby="guide-checkout-quiet">
          <SectionHeading id="guide-checkout-quiet">{t(L, 'guide.tileQuiet')}</SectionHeading>
          <HostText lang={lang}>{guide.quiet_hours}</HostText>
        </Panel>
      ) : null}
    </>
  );
};
